import { useMemo } from 'react';
import { useTasksUnified, type Task } from '@/domains/tasks/hooks/useTasksUnified';

export type TaskBadgeVariant = 'default' | 'warning' | 'danger';

const isSameDay = (a: Date, b: Date) => {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

const toDateSafe = (value?: string | Date | null): Date | null => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

const isOpen = (task: Task) => {
  // Treat as open if not completed and status not explicitly completed
  return !task.completed && task.status !== 'completed';
};

export const useOpenTasksCount = () => {
  const { allTasks } = useTasksUnified();

  return useMemo(() => {
    const now = new Date();

    let openCount = 0;
    let dueToday = 0;
    let overdue = 0;

    (allTasks || []).forEach((task) => {
      if (!isOpen(task)) return;
      openCount += 1;

      const deadline = toDateSafe(task.deadline ?? null);
      if (!deadline) return;
      if (deadline < now) {
        overdue += 1;
        return;
      }
      if (isSameDay(deadline, now)) {
        dueToday += 1;
      }
    });

    let badgeVariant: TaskBadgeVariant | undefined;
    if (overdue > 0) badgeVariant = 'danger';
    else if (dueToday > 0) badgeVariant = 'warning';
    else if (openCount > 0) badgeVariant = 'default';

    const displayCount = openCount > 99 ? 99 : openCount;

    return { openCount, dueToday, overdue, badgeVariant, displayCount };
  }, [allTasks]);
};
